/**
 * Employer Dashboard
 * Job posting with real-time AI feedback and improvement suggestions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Briefcase,
  FileText,
  BarChart3,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Zap,
  Eye,
  Send,
  RefreshCw,
  Loader2,
  Building2,
  MapPin,
  DollarSign,
  Users,
  TrendingUp,
  Sparkles,
  ChevronRight,
  Target,
  Shield,
  HelpCircle,
  PlusCircle,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCountry } from '../context/CountryContext';
import debounce from 'lodash/debounce';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Job Categories
const JOB_CATEGORIES = [
  { value: 'healthcare-care', label: 'Healthcare & Care', icon: '❤️' },
  { value: 'education-training', label: 'Education & Training', icon: '🎓' },
  { value: 'sales-marketing', label: 'Sales & Marketing', icon: '📈' },
  { value: 'customer-success', label: 'Customer Success', icon: '🤝' },
  { value: 'hr-jobs', label: 'HR & People', icon: '🧑‍💼' },
  { value: 'driver-delivery', label: 'Driver & Delivery', icon: '🚗' },
  { value: 'security', label: 'Security', icon: '🛡️' },
  { value: 'cleaning-domestic', label: 'Cleaning & Domestic', icon: '🧹' },
  { value: 'finance-accounting', label: 'Finance & Accounting', icon: '💰' },
  { value: 'technology-it', label: 'Technology & IT', icon: '💻' },
  { value: 'retail', label: 'Retail', icon: '🛍️' },
  { value: 'hospitality', label: 'Hospitality', icon: '🏨' },
  { value: 'construction-property', label: 'Construction & Property', icon: '🏗️' },
  { value: 'manufacturing-warehouse', label: 'Manufacturing & Warehouse', icon: '🏭' },
  { value: 'creative-media', label: 'Creative & Media', icon: '🎨' },
  { value: 'logistics-transport', label: 'Logistics & Transport', icon: '🚚' },
  { value: 'other', label: 'Other', icon: '💼' },
];

// Employment Types
const EMPLOYMENT_TYPES = [
  { value: 'full-time', label: 'Full Time' },
  { value: 'part-time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'freelance', label: 'Freelance' },
];

// Work Styles
const WORK_STYLES = [
  { value: 'on-site', label: 'On-site' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
];


// AI Score Display Component
const AIScoreDisplay = ({ confidence, scores, likelyOutcome, loading }) => {
  if (loading) {
    return (
      <Card className="border-2 border-dashed border-gray-200">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-500">Analyzing your listing...</span>
        </CardContent>
      </Card>
    );
  }

  if (!confidence) {
    return (
      <Card className="border-2 border-dashed border-gray-200 bg-gray-50">
        <CardContent className="p-6 text-center">
          <Sparkles className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">Start typing your job details to see AI feedback</p>
          <p className="text-sm text-gray-400 mt-1">Real-time suggestions will appear here</p>
        </CardContent>
      </Card>
    );
  }

  const confidenceColor = confidence.confidence_color === 'green' ? 'border-green-400 bg-green-50' :
                          confidence.confidence_color === 'amber' ? 'border-amber-400 bg-amber-50' :
                          'border-red-400 bg-red-50';

  return (
    <div className="space-y-4">
      {/* Main Score Card */}
      <Card className={`border-2 ${confidenceColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                confidence.confidence_color === 'green' ? 'bg-green-100' :
                confidence.confidence_color === 'amber' ? 'bg-amber-100' : 'bg-red-100'
              }`}>
                <span className={`text-2xl font-bold ${
                  confidence.confidence_color === 'green' ? 'text-green-600' :
                  confidence.confidence_color === 'amber' ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {confidence.confidence_score}%
                </span>
              </div>
              <div>
                <p className="font-semibold text-lg">AI Confidence Score</p>
                <Badge className={
                  confidence.confidence_color === 'green' ? 'bg-green-500' :
                  confidence.confidence_color === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                }>
                  {confidence.confidence_label}
                </Badge>
              </div>
            </div>
            
            {likelyOutcome && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Likely Outcome</p>
                <Badge variant="outline" className={`capitalize ${
                  likelyOutcome.decision === 'approve' ? 'border-green-500 text-green-600' :
                  likelyOutcome.decision === 'review' ? 'border-amber-500 text-amber-600' :
                  'border-red-500 text-red-600'
                }`}>
                  {likelyOutcome.decision === 'approve' ? '✓ Likely Approved' :
                   likelyOutcome.decision === 'review' ? '? Manual Review' :
                   likelyOutcome.decision === 'hold' ? '⚠ Needs Fixes' : '✗ Unlikely'}
                </Badge>
              </div>
            )}
          </div>
          
          <p className="text-sm text-gray-600">{confidence.confidence_message}</p>
          
          {/* Score Breakdown */}
          {scores && (
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-white rounded-lg">
                <p className="text-xs text-gray-500">Fraud Risk</p>
                <p className={`font-bold ${scores.fraud_score < 30 ? 'text-green-600' : scores.fraud_score < 60 ? 'text-amber-600' : 'text-red-600'}`}>
                  {scores.fraud_score}
                </p>
              </div>
              <div className="p-2 bg-white rounded-lg">
                <p className="text-xs text-gray-500">Compliance</p>
                <p className={`font-bold ${scores.compliance_score < 30 ? 'text-green-600' : scores.compliance_score < 50 ? 'text-amber-600' : 'text-red-600'}`}>
                  {scores.compliance_score}
                </p>
              </div>
              <div className="p-2 bg-white rounded-lg">
                <p className="text-xs text-gray-500">Combined</p>
                <p className={`font-bold ${scores.combined_score < 30 ? 'text-green-600' : scores.combined_score < 60 ? 'text-amber-600' : 'text-red-600'}`}>
                  {scores.combined_score}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Required Fixes */}
      {confidence.required_fixes?.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4" />
              Required Fixes ({confidence.required_fixes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {confidence.required_fixes.map((fix, idx) => (
              <div key={idx} className="p-3 bg-white rounded-lg border border-red-200">
                <p className="font-medium text-red-800 text-sm">{fix.title}</p>
                <p className="text-xs text-red-600 mt-1">{fix.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Quick Wins */}
      {confidence.quick_wins?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
              <Zap className="h-4 w-4" />
              Quick Wins ({confidence.quick_wins.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {confidence.quick_wins.map((win, idx) => (
              <div key={idx} className="p-3 bg-white rounded-lg border border-amber-200">
                <p className="font-medium text-amber-800 text-sm">{win.title}</p>
                <p className="text-xs text-amber-600 mt-1">{win.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* General Suggestions */}
      {confidence.suggestions?.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
              <CheckCircle2 className="h-4 w-4" />
              Suggestions ({confidence.suggestions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {confidence.suggestions.slice(0, 3).map((sug, idx) => (
              <div key={idx} className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="font-medium text-blue-800 text-sm">{sug.title}</p>
                <p className="text-xs text-blue-600 mt-1">{sug.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// Job Form Component
const JobPostingForm = ({ onPreview, previewData, previewLoading }) => {
  const { country } = useCountry();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    salary_min: '',
    salary_max: '',
    salary_period: 'per_annum',
    employment_type: 'full-time',
    work_style: 'on-site',
    company_name: '',
    company_email: '',
    requirements: '',
    benefits: '',
  });

  // Debounced preview function
  const debouncedPreview = useCallback(
    debounce((data) => {
      if (data.title && data.description && data.category) {
        onPreview(data);
      }
    }, 1000),
    [onPreview]
  );

  // Update form and trigger preview
  const handleChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    debouncedPreview({ ...newData, country });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.category) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in title, description, and category',
        variant: 'destructive',
      });
      return;
    }

    // Check if there are required fixes
    if (previewData?.confidence?.required_fixes?.length > 0) {
      toast({
        title: 'Required Fixes Pending',
        description: 'Please address the required fixes before submitting',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      
      const jobData = {
        ...formData,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
        country,
        status: 'active',
        moderation_status: 'pending',
      };

      const response = await axios.post(`${API_URL}/api/jobs`, jobData);
      
      if (response.data.success || response.data.id) {
        toast({
          title: 'Job Submitted!',
          description: 'Your job has been submitted for review. You\'ll be notified once approved.',
        });
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          category: '',
          location: '',
          salary_min: '',
          salary_max: '',
          salary_period: 'per_annum',
          employment_type: 'full-time',
          work_style: 'on-site',
          company_name: '',
          company_email: '',
          requirements: '',
          benefits: '',
        });
      }
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Failed to submit job',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategory = JOB_CATEGORIES.find(c => c.value === formData.category);

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            Job Category
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {JOB_CATEGORIES.slice(0, 5).map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleChange('category', cat.value)}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  formData.category === cat.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <span className="text-2xl block mb-1">{cat.icon}</span>
                <span className="text-xs font-medium">{cat.label}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
            {JOB_CATEGORIES.slice(5).map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleChange('category', cat.value)}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  formData.category === cat.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <span className="text-2xl block mb-1">{cat.icon}</span>
                <span className="text-xs font-medium">{cat.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Job Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Job Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Job Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Senior Care Assistant, Registered Nurse"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="description">
              Job Description *
              <span className="text-xs text-gray-500 ml-2">
                ({formData.description.length} characters - aim for 300+)
              </span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the role, responsibilities, and what makes this opportunity special..."
              className="mt-1 min-h-[150px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., London, Manchester"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="work_style">Work Style</Label>
              <Select value={formData.work_style} onValueChange={(v) => handleChange('work_style', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORK_STYLES.map((style) => (
                    <SelectItem key={style.value} value={style.value}>
                      {style.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="salary_min">Salary Min (£)</Label>
              <Input
                id="salary_min"
                type="number"
                value={formData.salary_min}
                onChange={(e) => handleChange('salary_min', e.target.value)}
                placeholder="e.g., 25000"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="salary_max">Salary Max (£)</Label>
              <Input
                id="salary_max"
                type="number"
                value={formData.salary_max}
                onChange={(e) => handleChange('salary_max', e.target.value)}
                placeholder="e.g., 35000"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="employment_type">Employment Type</Label>
              <Select value={formData.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Your company name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="company_email">Contact Email</Label>
              <Input
                id="company_email"
                type="email"
                value={formData.company_email}
                onChange={(e) => handleChange('company_email', e.target.value)}
                placeholder="careers@company.com"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="requirements">Requirements</Label>
            <Textarea
              id="requirements"
              value={formData.requirements}
              onChange={(e) => handleChange('requirements', e.target.value)}
              placeholder="List qualifications, experience, certifications required..."
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="benefits">Benefits</Label>
            <Textarea
              id="benefits"
              value={formData.benefits}
              onChange={(e) => handleChange('benefits', e.target.value)}
              placeholder="List benefits: pension, holidays, training, flexible hours..."
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => onPreview({ ...formData, country })}
          disabled={!formData.title || !formData.description || !formData.category}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Preview
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || previewData?.confidence?.required_fixes?.length > 0}
          className="bg-green-600 hover:bg-green-700"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Submit for Review
            </>
          )}
        </Button>
      </div>
    </div>
  );
};


// My Jobs List Component
const MyJobsList = ({ employerId }) => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    fetchJobs();
  }, [employerId]);

  const fetchJobs = async () => {
    if (!employerId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/healthcare-moderation/employer/my-jobs?employer_id=${employerId}`
      );
      
      if (response.data.success) {
        setJobs(response.data.jobs || []);
        setStats(response.data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Pending Review' },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
      flagged: { color: 'bg-amber-100 text-amber-700', icon: AlertTriangle, label: 'Flagged' },
      needs_info: { color: 'bg-blue-100 text-blue-700', icon: HelpCircle, label: 'Info Requested' },
    };
    
    const cfg = config[status] || config.pending;
    const Icon = cfg.icon;
    
    return (
      <Badge className={cfg.color}>
        <Icon className="h-3 w-3 mr-1" />
        {cfg.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { key: 'pending', label: 'Pending', color: 'bg-gray-100' },
          { key: 'approved', label: 'Approved', color: 'bg-green-100' },
          { key: 'rejected', label: 'Rejected', color: 'bg-red-100' },
          { key: 'flagged', label: 'Flagged', color: 'bg-amber-100' },
          { key: 'needs_info', label: 'Info Needed', color: 'bg-blue-100' },
        ].map((stat) => (
          <Card key={stat.key} className={stat.color}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats[stat.key] || 0}</p>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Jobs List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>My Job Listings</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchJobs}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No jobs posted yet</p>
              <p className="text-sm">Create your first job listing above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{job.title}</h4>
                      <p className="text-sm text-gray-500">
                        {job.category} • {job.location || 'Location not set'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Posted: {new Date(job.created_at).toLocaleDateString()}
                        {job.moderated_at && ` • Reviewed: ${new Date(job.moderated_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {job.ai_fraud_score !== undefined && (
                        <div className="text-center">
                          <p className="text-xs text-gray-500">AI Score</p>
                          <p className={`font-bold ${
                            job.ai_fraud_score < 30 ? 'text-green-600' :
                            job.ai_fraud_score < 60 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {job.ai_fraud_score}
                          </p>
                        </div>
                      )}
                      {getStatusBadge(job.moderation_status)}
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex gap-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" /> {job.views || 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" /> {job.applications || 0} applications
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// Main Employer Dashboard Component
const EmployerDashboard = () => {
  const { toast } = useToast();
  const { country } = useCountry();
  const [activeTab, setActiveTab] = useState('post');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Get employer ID from localStorage or context
  const employerId = localStorage.getItem('user_id') || 'demo-employer';

  const handlePreview = async (jobData) => {
    try {
      setPreviewLoading(true);
      
      const response = await axios.post(
        `${API_URL}/api/admin/healthcare-moderation/employer/preview-score`,
        {
          ...jobData,
          salary_min: jobData.salary_min ? parseInt(jobData.salary_min) : null,
          salary_max: jobData.salary_max ? parseInt(jobData.salary_max) : null,
        }
      );
      
      if (response.data.success) {
        setPreviewData(response.data);
      }
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="employer-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Building2 className="h-8 w-8" />
                Employer Dashboard
              </h1>
              <p className="text-blue-100 mt-1">Post jobs with AI-powered feedback</p>
            </div>
            <Badge className="bg-white/20 text-white">
              <Shield className="h-4 w-4 mr-1" />
              AI-Assisted Posting
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="post" className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Post New Job
            </TabsTrigger>
            <TabsTrigger value="my-jobs" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              My Jobs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="post">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form Column */}
              <div className="lg:col-span-2">
                <JobPostingForm
                  onPreview={handlePreview}
                  previewData={previewData}
                  previewLoading={previewLoading}
                />
              </div>

              {/* AI Feedback Column */}
              <div className="lg:col-span-1">
                <div className="sticky top-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                    AI Feedback (Live)
                  </h3>
                  <AIScoreDisplay
                    confidence={previewData?.confidence}
                    scores={previewData?.scores}
                    likelyOutcome={previewData?.likely_outcome}
                    loading={previewLoading}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="my-jobs">
            <MyJobsList employerId={employerId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmployerDashboard;
